// Saved chats from the v3 Conversations routes (API-3641), turned into chat-reducer messages so a
// reopened chat renders with the same components as a live one, already folded.

import { alertTurn } from './alerts'

const STATUS = { complete: 'complete', failed: 'failed', cancelled: 'stopped', streaming: 'saving' }

const time = iso => {
  const t = Date.parse(iso || '')
  return Number.isFinite(t) ? t : null
}

const mapStep = s => ({
  stepId: s.StepId,
  kind: s.Kind || 'tool',
  status: s.Status || 'succeeded',
  title: s.Title || '',
  summary: s.Summary || null,
  tool: s.Tool || null,
  message: s.Message || null,
  thinking: s.Thinking || '',
  result: s.Status === 'succeeded' && s.Result && typeof s.Result === 'object' ? s.Result : null,
  // What Claude asked the tool for (group chats): a client action's change, for "Apply" on another map.
  ...(s.Input && typeof s.Input === 'object' ? { input: s.Input } : {})
})

const mapCitation = c => ({
  index: Number(c.Index),
  title: c.Title || '',
  url: c.Url || null,
  citeUrl: c.CiteUrl || null,
  locator: c.Locator || null,
  sourceType: c.SourceType || null,
  asOf: c.AsOf || null
})

// One chat message per question: the user's message and the Artemis reply to it (ReplyToId).
// items may come in any order and from several pages; they are sorted by Seq. A reply whose
// question isn't loaded yet is left out until it is.
//   status 'saving': the server was still writing the reply when it was fetched.
// In a group (options.group), a message that didn't mention Artemis gets no reply: it's 'posted'
// rather than a failed turn. Every turn carries who wrote it (authorUserId, authorName) and who
// it mentions (mentions: [userId | 'artemis']).
// In the alerts conversation each alert (AuthorType "alert") is a turn of its own, in Seq order
// between the questions (lib/artemis/alerts alertTurn).
export const messagesFromConversation = (items = [], options = {}) => {
  const sorted = [...items].sort((a, b) => a.Seq - b.Seq)
  const replies = new Map()
  sorted.forEach(m => { if (m.AuthorType === 'assistant' && m.ReplyToId != null) replies.set(m.ReplyToId, m) })

  return sorted.filter(m => m.AuthorType === 'user' || m.AuthorType === 'alert').map(question => {
    if (question.AuthorType === 'alert') return alertTurn(question)
    const reply = replies.get(question.Id)
    const base = {
      id: `v3-${question.Id}`,
      question: question.Text || '',
      questionMessageId: question.Id,
      seq: question.Seq,
      sources: [],
      suggestedQuestions: [],
      stats: null,
      tools: [],
      actions: [],
      statusText: null,
      // How long it thought isn't saved, so reopened replies just count their tools.
      startedAt: null,
      authorUserId: question.AuthorUserId ?? null,
      authorName: question.AuthorName || null,
      mentions: Array.isArray(question.Mentions) ? question.Mentions : []
    }
    const forArtemis = base.mentions.some(m => String(m).toLowerCase() === 'artemis')
    if (!reply && options.group && !forArtemis) return { ...base, answer: '', status: 'posted', queryId: null, messageId: null, steps: [], citations: [], answerStartedAt: null, endedAt: null }
    if (!reply) return { ...base, answer: '', status: 'failed', errorCode: 'no_reply', queryId: null, messageId: null, steps: [], citations: [], answerStartedAt: null, endedAt: null }
    return {
      ...base,
      answer: reply.Html || '',
      status: STATUS[reply.Status] || 'complete',
      errorCode: reply.Status === 'failed' ? 'internal_error' : undefined,
      queryId: reply.QueryId || null,
      messageId: reply.Id,
      steps: (reply.Steps || []).map(mapStep),
      citations: (reply.Citations || []).map(mapCitation).filter(c => Number.isInteger(c.index)),
      answerStartedAt: null,
      endedAt: null,
      // A reply still being written (a partner's, polled) counts its time from when it began.
      startedAt: reply.Status === 'streaming' ? (Date.parse(reply.CreatedAt || '') || null) : null,
      // Staging only (API-3693). Stats (model, tokens, timings) show once the server saves them
      // with the message; today only the cost is saved.
      stats: reply.Stats && typeof reply.Stats === 'object' ? reply.Stats : null,
      cost: cost(reply.EstimatedCostUsd) ?? cost(reply.Stats && reply.Stats.EstimatedCostUsd)
    }
  })
}

const cost = v => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null)

// Merges older messages ahead of what's loaded, skipping any already there.
export const mergeEarlier = (older, current) => {
  const ids = new Set(current.map(m => m.id))
  const replies = new Set(current.map(m => m.messageId).filter(Boolean))
  return [...older.filter(m => !ids.has(m.id) && !(m.messageId && replies.has(m.messageId))), ...current]
}

// A Conversation as a past-chat row: { id, title, sub, at }.
export const conversationRow = c => ({
  id: c.Id,
  kind: c.Kind || 'direct',
  members: Array.isArray(c.Members) ? c.Members : [],
  unread: Number(c.UnreadCount) || 0,
  unreadMentions: Number(c.UnreadMentions) || 0,
  title: c.Title || 'New chat',
  preview: c.LastMessagePreview || '',
  at: time(c.LastMessageAt) || time(c.CreatedAt),
  cost: cost(c.EstimatedCostUsd),
  archived: c.Archived === true,
  // The scratchpad (API-3909): whether it's on, and how many items it holds and has sent. Left out
  // (undefined) by servers without it.
  ...(typeof c.ScratchpadOn === 'boolean' && { scratchpadOn: c.ScratchpadOn }),
  ...(Number.isFinite(c.ScratchCount) && { scratchCount: c.ScratchCount }),
  ...(Number.isFinite(c.ScratchSentCount) && { scratchSentCount: c.ScratchSentCount })
})

export const newClientMessageId = () => {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
  // RFC 4122 v4 from Math.random, for older browsers.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = Math.random() * 16 | 0
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}
